import React, { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../hooks/usePermissions';
import { updateMessageFilters } from '../services/tenantService';
import { Save, CheckCircle, XCircle, Loader } from 'lucide-react';
import { AdminPageLayout } from './AdminPageLayout';
import { AdminCard } from './AdminCard';

export function TenantMessageFilters() {
  const { tenant, tenantConfig, refreshConfig } = useTenant();
  const { canUpdateTenantConfig } = usePermissions();
  const canEdit = canUpdateTenantConfig();
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const [enabled, setEnabled] = useState(false);
  const [thresholdDays, setThresholdDays] = useState(30);

  // Load existing config
  useEffect(() => {
    if (tenantConfig?.messageFilters) {
      setEnabled(tenantConfig.messageFilters.enabled || false);
      setThresholdDays(tenantConfig.messageFilters.criteria?.activeRoomThresholdDays || 30);
    }
  }, [tenantConfig]);

  const handleSave = async () => {
    if (!tenant?._id) {
      alert('Tenant not found');
      return;
    }

    setLoading(true);
    setSaveStatus('saving');

    try {
      await updateMessageFilters(tenant._id, {
        enabled,
        criteria: {
          syncActiveRoomsOnly: true,
          activeRoomThresholdDays: thresholdDays,
        },
      });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
      await refreshConfig();
    } catch (error) {
      console.error('Error saving message filters:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
      alert(`Failed to save message filters: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (saveStatus === 'saving') return <Loader className="w-4 h-4 animate-spin" />;
    if (saveStatus === 'success') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (saveStatus === 'error') return <XCircle className="w-4 h-4 text-red-500" />;
    return null;
  };

  const statusIcon = getStatusIcon();

  return (
    <AdminPageLayout>
      <AdminCard bodyClassName="space-y-4">
        {!canEdit && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
            You have read-only access. Contact an admin to update these settings.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Enable/Disable Toggle */}
          <div className="admin-filter-panel">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={!canEdit}
                className="h-3.5 w-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Enable room filtering</span>
            </label>
            <p className="text-xs text-gray-400 mt-2">
              When disabled, all rooms are synced regardless of activity.
            </p>
          </div>

          {/* Threshold Days */}
          <div className="admin-filter-panel">
            <label className="admin-filter-label">
              Active Room Threshold (days)
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={thresholdDays}
              onChange={(e) => setThresholdDays(Math.max(1, parseInt(e.target.value) || 30))}
              disabled={!enabled || !canEdit}
              className="admin-filter-control disabled:bg-gray-50 disabled:text-gray-400"
            />
            <p className="text-xs text-gray-400 mt-2">
              Only sync rooms with activity within the last {thresholdDays} days. Older rooms will be skipped during sync.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
          {statusIcon && <span className="text-gray-500">{statusIcon}</span>}
          <button
            onClick={handleSave}
            disabled={loading || !canEdit}
            className="px-3 py-1 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
          >
            <Save className="w-4 h-4" />
            Save Filters
          </button>
        </div>
      </AdminCard>
    </AdminPageLayout>
  );
}
